import { ref, watch, SetupContext, readonly, Ref } from "vue";
import PagedAsyncIterDriver from "@/pagination/paged-async-iter-driver";

export interface ControlPaginationExports<T> {
  currentPage: Ref<number>;
  cantPageInputVal: Ref<string | number>;
  pageSize: Ref<number>;
  loadingData: Ref<boolean>;
  elements: Ref<T[]>;
  fetchData: (pageNum: number) => Promise<void>;
  setDriver: (asyncIterDriver: PagedAsyncIterDriver<T>) => void;
  driver: Ref<null | PagedAsyncIterDriver<T>>;
  setCurrentPage?: (page: number) => Promise<void>;
}

export default function <T>(
  emit: SetupContext["emit"],
  fetchOnPageChange = true
): ControlPaginationExports<T> {
  const currentPage = ref(0);
  const _pageSize = ref(10);
  const pageSize = readonly(_pageSize);
  const loadingData = ref(false);

  // Casting to ref with Generic T to Ref per recommendation of the docs.
  // https://v3.vuejs.org/guide/typescript-support.html#typing-refs
  const elements = ref<T[]>([]) as Ref<T[]>;
  type DriverType = null | PagedAsyncIterDriver<T>;
  const driver = ref<DriverType>(null) as Ref<DriverType>;
  const cantPageInputVal = ref<string | number>(10);

  const fetchData = async (pageNum: number) => {
    loadingData.value = true;
    try {
      if (!driver.value) {
        return;
      }
      const neededElements = _pageSize.value * pageNum;
      if (driver.value.fetchedElementsCount >= neededElements) {
        return;
      }
      const elementsBuffer: T[] = await driver.value?.fetchUntilFetchedCount(
        neededElements
      );
      if (elementsBuffer) {
        elements.value.push(...(elementsBuffer as T[]));
      }
    } finally {
      loadingData.value = false;
    }
  };

  watch(loadingData, (newVal, oldVal) => {
    if (newVal == true && oldVal == false) {
      emit("loadingData", true);
    } else {
      emit("loadingData", false);
    }
  });
  let setCurrentPage;
  if (fetchOnPageChange) {
    watch(currentPage, async (newVal) => {
      await fetchData(newVal);
    });
  } else {
    setCurrentPage = async (page: number) => {
      currentPage.value = page;
      await fetchData(page);
    };
  }
  watch(_pageSize, async () => {
    await fetchData(currentPage.value);
  });
  watch(currentPage, (newVal) => {
    emit("currentPageChange", newVal);
  });
  watch(cantPageInputVal, (newVal, oldVal) => {
    switch (typeof newVal) {
      case "number":
        _pageSize.value = newVal;
        break;
      case "string":
        // Add lexical scope to bind `const parsed`
        {
          if (newVal.trim() !== "") {
            const parsed = parseInt(newVal);
            if (parsed > 0) {
              _pageSize.value = parsed;
            }
          } else {
            if (typeof oldVal === "string") {
              _pageSize.value = parseInt(oldVal);
            } else {
              _pageSize.value = oldVal;
            }
          }
        }
        break;
    }
    cantPageInputVal.value = _pageSize.value;
  });

  const setDriver = (asyncDriver: PagedAsyncIterDriver<T>) => {
    driver.value = asyncDriver;
  };

  return {
    currentPage,
    cantPageInputVal,
    pageSize,
    loadingData,
    elements,
    fetchData,
    setDriver,
    driver,
    setCurrentPage,
  };
}
